import React, { useEffect, useMemo } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "./App.css";

import {
  ThemeProvider as CustomThemeProvider,
  useCustomTheme,
} from "./components/ThemeContext";
import { themeOptions } from "./customTheme";

import { AuthProvider } from "./components/Auth/AuthContext";
import PrivateRoute from "./components/Auth/PrivateRoute";
import SignInScreen from "./components/Auth/SignInScreen";
import SignUpScreen from "./components/Auth/SignUpScreen";
import ForgotPasswordForm from "./components/Auth/ForgotPasswordForm";

import Home from "./components/MarketingSite/Home";
import About from "./components/MarketingSite/About";

import DeedList from "./components/Deed/DeedList/DeedList";
import { DeedsUIProvider } from "./components/Deed/DeedsUIContext";
import {
  ChangeAllBucketsModalProvider,
  ChangeAllBucketsModalContext,
} from "./components/Deed/ChangeAllBuckets/ChangeAllBucketsModalContext";
import ChangeAllBucketsModal from "./components/Deed/ChangeAllBuckets/ChangeAllBucketsModal";
import ErrorBoundary from "./components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const darkPublicTheme = createTheme({ palette: { mode: "dark" } });

function App() {
  useEffect(() => {
    document.title = import.meta.env.REACT_APP_TITLE || "memorloko";
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CustomThemeProvider>
        <AppContent />
      </CustomThemeProvider>
    </QueryClientProvider>
  );
}

const PublicRoute = ({ children }) => (
  <ThemeProvider theme={darkPublicTheme}>
    <CssBaseline />
    {children}
  </ThemeProvider>
);

const AppContent = () => {
  const { theme } = useCustomTheme();

  const muiTheme = useMemo(
    () =>
      createTheme({
        ...themeOptions,
        palette: { ...themeOptions.palette, mode: theme },
      }),
    [theme]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <div className="App">
        <Router>
          <AuthProvider>
            <ErrorBoundary>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Home />
                  </PublicRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <PublicRoute>
                    <About />
                  </PublicRoute>
                }
              />
              <Route
                path="/signin"
                element={
                  <PublicRoute>
                    <SignInScreen />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUpScreen />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgotpassword"
                element={
                  <PublicRoute>
                    <ForgotPasswordForm />
                  </PublicRoute>
                }
              />
              <Route element={<PrivateRoute />}>
                <Route
                  path="/deeds"
                  element={
                    <DeedsUIProvider>
                      <ChangeAllBucketsModalProvider>
                        <DeedList />
                        <ChangeAllBucketsModalSlot />
                      </ChangeAllBucketsModalProvider>
                    </DeedsUIProvider>
                  }
                />
              </Route>
            </Routes>
            </ErrorBoundary>
          </AuthProvider>
        </Router>
      </div>
    </ThemeProvider>
  );
};

const ChangeAllBucketsModalSlot = () => {
  const { isChangeAllBucketsModalOpen } = React.useContext(
    ChangeAllBucketsModalContext
  );
  return isChangeAllBucketsModalOpen ? <ChangeAllBucketsModal /> : null;
};

export default App;
